import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Check, Minus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Eyebrow,
  MarketingLayout,
  SectionHeader,
  I18nPageHero,
} from "@/components/marketing/Layout";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { supabase } from "@/integrations/supabase/client";

type BillingCycle = "monthly" | "yearly";
type Currency = "usd" | "chf" | "eur";

const CURRENCY_SYMBOL: Record<Currency, string> = { usd: "$", chf: "CHF ", eur: "€" };

// Tier index → per-currency, per-cycle lookup_key. null = no checkout (Free).
const PRICE_MAP: Array<Record<Currency, { monthly: string; yearly: string }> | null> = [
  null,
  {
    usd: { monthly: "starter_monthly", yearly: "starter_yearly" },
    chf: { monthly: "starter_monthly_chf", yearly: "starter_yearly_chf" },
    eur: { monthly: "starter_monthly_eur", yearly: "starter_yearly_eur" },
  },
  {
    usd: { monthly: "pro_monthly", yearly: "pro_yearly" },
    chf: { monthly: "pro_monthly_chf", yearly: "pro_yearly_chf" },
    eur: { monthly: "pro_monthly_eur", yearly: "pro_yearly_eur" },
  },
  {
    usd: { monthly: "scale_monthly", yearly: "scale_yearly" },
    chf: { monthly: "scale_monthly_chf", yearly: "scale_yearly_chf" },
    eur: { monthly: "scale_monthly_eur", yearly: "scale_yearly_eur" },
  },
];

// Honest status per comparison row (indexed by position — the row order is
// identical across all locales). The strategy review flagged exactly this:
// selling a capability on the pricing page that the changelog calls upcoming.
// Anything not fully shipped is labelled here, so the table can't over-promise.
// null = live, no badge needed. Keep in sync with lib/feature-status.ts.
const ROW_STATUS: (null | "beta" | "planned")[] = [
  null, // AI Twins
  null, // Conversations / month
  null, // Knowledge sources
  null, // Lead capture & scoring
  "beta", // Twin-to-Twin protocol
  null, // Opportunity engine
  null, // Marketplace presence
  "planned", // CRM sync (Salesforce, HubSpot) — webhooks ship today, native sync doesn't
  null, // Buy extra conversations
  null, // Support
];

const AMOUNTS: Record<number, { monthly: number; yearly: number } | null> = {
  0: null,
  1: { monthly: 39, yearly: 390 },
  2: { monthly: 149, yearly: 1490 },
  3: { monthly: 399, yearly: 3990 },
};

export const Route = createFileRoute("/pricing")({
  head: () => {
    const seo = buildSeo({
      path: "/pricing",
      title: "Pricing — Reality Twin Network · from free to scale",
      description:
        "Transparent AI Twin pricing: Free, Starter, Pro and Scale, with one shared conversation pool across all your twins.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Reality Twin Network",
            description:
              "AI Twin platform for modern companies — turns your website into a conversational digital twin.",
            brand: { "@type": "Brand", name: "Reality Twin" },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "CHF",
              lowPrice: "0",
              highPrice: "3990",
              offerCount: "4",
              url: "https://realitytwin.io/pricing",
            },
          }),
        },
      ],
    };
  },
  component: PricingPage,
});

function PricingPage() {
  const { t } = useTranslation();
  const tiers = t("routes.pricing.tiers", { returnObjects: true }) as {
    name: string;
    price: string;
    desc: string;
    feats: string[];
    cta: string;
  }[];
  const rows = t("routes.pricing.rows", { returnObjects: true }) as {
    label: string;
    values: (string | boolean)[];
  }[];
  const perMo = t("routes.pricing.perMo");
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [currency, setCurrency] = useState<Currency>("chf");
  const [userEmail, setUserEmail] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const { openCheckout, checkoutElement, closeCheckout, isOpen } = useStripeCheckout();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email ?? undefined);
      setUserId(data.user?.id ?? undefined);
    });
  }, []);

  const priceLabel = (idx: number, base: string) => {
    const amt = AMOUNTS[idx];
    if (!amt) return base;
    const n = cycle === "monthly" ? amt.monthly : amt.yearly;
    return `${CURRENCY_SYMBOL[currency]}${n}`;
  };

  const handleCta = (idx: number) => {
    const price = PRICE_MAP[idx];
    if (!price) return;
    if (!userId) {
      window.location.href = `/auth?next=${encodeURIComponent("/pricing")}`;
      return;
    }
    openCheckout({
      priceId: price[currency][cycle],
    });
  };

  return (
    <MarketingLayout>
      <I18nPageHero tKey="pages.pricing" />

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <div className="inline-flex items-center rounded-full hairline bg-background p-1 text-[13px]">
              <button
                onClick={() => setCycle("monthly")}
                className={`px-4 py-2 rounded-full transition ${cycle === "monthly" ? "bg-foreground text-background" : "text-muted-foreground"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setCycle("yearly")}
                className={`px-4 py-2 rounded-full transition ${cycle === "yearly" ? "bg-foreground text-background" : "text-muted-foreground"}`}
              >
                Yearly <span className="ml-1 opacity-70">— save 2 months</span>
              </button>
            </div>
            <div className="inline-flex items-center rounded-full hairline bg-background p-1 text-[13px]">
              {(["chf", "eur", "usd"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-2 rounded-full transition uppercase ${currency === c ? "bg-foreground text-background" : "text-muted-foreground"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border hairline rounded-xl overflow-hidden">
            {tiers.map((tier, idx) => {
              const featured = idx === 2;
              const isFree = idx === 0;
              const hasCheckout = !!PRICE_MAP[idx];
              const shownPrice = priceLabel(idx, tier.price);
              const suffix = AMOUNTS[idx] ? (cycle === "monthly" ? perMo : "/yr") : "";
              return (
                <div
                  key={tier.name}
                  className={`relative bg-card p-7 flex flex-col ${featured ? "bg-cream" : ""}`}
                >
                  {featured && (
                    <div className="absolute top-5 right-5 font-mono-eyebrow !text-[10px] text-foreground">
                      {t("routes.pricing.popular")}
                    </div>
                  )}
                  <div className="text-[13px] text-muted-foreground">{tier.name}</div>
                  <div className="mt-3 font-serif text-[44px] leading-none">
                    {shownPrice}
                    {suffix && (
                      <span className="text-[14px] text-muted-foreground font-sans">{suffix}</span>
                    )}
                  </div>
                  <div className="mt-2 text-[13px] text-muted-foreground">{tier.desc}</div>
                  <ul className="mt-5 space-y-2 text-[13.5px] flex-1">
                    {tier.feats.map((f) => (
                      <li key={f} className="flex gap-2 items-start">
                        <Check className="h-3.5 w-3.5 mt-1 shrink-0" strokeWidth={2.5} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {hasCheckout ? (
                    <button
                      onClick={() => handleCta(idx)}
                      className={`mt-7 inline-flex justify-center items-center rounded-full px-4 py-2.5 text-[13px] font-medium transition ${
                        featured
                          ? "bg-foreground text-background hover:opacity-90"
                          : "hairline bg-background hover:bg-cream"
                      }`}
                    >
                      {tier.cta}
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      className={`mt-7 inline-flex justify-center items-center rounded-full px-4 py-2.5 text-[13px] font-medium transition ${
                        featured
                          ? "bg-foreground text-background hover:opacity-90"
                          : "hairline bg-background hover:bg-cream"
                      }`}
                    >
                      {tier.cta}
                    </Link>
                  )}
                  {isFree && (
                    <div className="mt-2 text-[11px] text-muted-foreground text-center">
                      No credit card required
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4"
          onClick={closeCheckout}
        >
          <div
            className="bg-background rounded-xl w-full max-w-2xl mt-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 py-3 border-b border-border">
              <div className="text-sm font-medium">Checkout</div>
              <button
                onClick={closeCheckout}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-2">{checkoutElement}</div>
          </div>
        </div>
      )}

      <section className="bg-cream border-y border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("routes.pricing.compareEyebrow")}
            title={
              <>
                {t("routes.pricing.compareTitleA")}
                <em className="italic text-muted-foreground">
                  {t("routes.pricing.compareTitleEm")}
                </em>
              </>
            }
          />
          <div className="mt-14 hairline rounded-xl bg-background overflow-x-auto">
            <table className="w-full text-[14px] min-w-[720px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-medium px-5 py-4 w-[34%]">
                    {t("routes.pricing.featureHeader")}
                  </th>
                  {tiers.map((tier) => (
                    <th key={tier.name} className="text-left font-medium px-5 py-4">
                      {tier.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={row.label} className="border-b border-border last:border-b-0">
                    <td className="px-5 py-3.5 text-muted-foreground">
                      {row.label}
                      {ROW_STATUS[ri] && (
                        <span
                          className={`ml-2 align-middle text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            ROW_STATUS[ri] === "beta"
                              ? "bg-amber-500/15 text-amber-700"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {t(`routes.pricing.st_${ROW_STATUS[ri]}`, {
                            defaultValue: ROW_STATUS[ri] as string,
                          })}
                        </span>
                      )}
                    </td>
                    {row.values.map((v, i) => (
                      <td key={i} className="px-5 py-3.5">
                        {typeof v === "boolean" ? (
                          v ? (
                            <Check className="h-4 w-4" strokeWidth={2.5} />
                          ) : (
                            <Minus className="h-4 w-4 text-muted-foreground/40" />
                          )
                        ) : (
                          <span>{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Eyebrow>{t("routes.pricing.qEyebrow")}</Eyebrow>
          <h2 className="mt-3 font-serif text-[40px] sm:text-[52px] leading-[1.02] tracking-[-0.025em]">
            {t("routes.pricing.qTitle")}
          </h2>
          <p className="mt-5 text-[16px] text-muted-foreground max-w-md mx-auto">
            {t("routes.pricing.qSub")}
          </p>
          <div className="mt-8 flex justify-center gap-2.5">
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition"
            >
              {t("routes.pricing.qCta")}
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
